import React, { useState } from 'react';
import { MultiSelect, Button } from '@mantine/core';

type TagData = {
  [orgName: string]: {
    tags: any;
    [compName: string]: {
      [siteName: string]: string[];
    };
  };
};

type Organization = {
  organization: string;
  companies: Company[];
};

type Company = {
  companyName: string;
  websites: Website[];
};

type Website = {
  siteName: string;
};

const TagManager: React.FC = () => {
  const [tags, setTags] = useState<TagData>({});
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const data: Organization[] = [
    {
      organization: "Organization A",
      companies: [
        {
          companyName: "Company A1",
          websites: [
            { siteName: "Site A1-1" },
            { siteName: "Site A1-2" }
          ]
        },
        {
          companyName: "Company A2",
          websites: [
            { siteName: "Site A2-1" }
          ]
        }
      ]
    },
    {
      organization: "Organization B",
      companies: [
        {
          companyName: "Company B1",
          websites: [
            { siteName: "Site B1-1" },
            { siteName: "Site B1-2" },
            { siteName: "Site B1-3" }
          ]
        }
      ]
    }
  ];

  const handleAddTag = (orgName: string, compName: string, siteName: string) => {
    setTags((prevTags) => {
      const updatedTags:any = { ...prevTags };
      const newTags = Array.from(new Set([...selectedTags]));

      // Update tags at the organization level
      if (!updatedTags[orgName]) updatedTags[orgName] = { tags: [] };
      updatedTags[orgName].tags = Array.from(new Set([...updatedTags[orgName].tags, ...newTags]));

      // Update tags for the specific website
      if (!updatedTags[orgName][compName]) updatedTags[orgName][compName] = {};
      const existingTags = updatedTags[orgName][compName][siteName] || [];
      updatedTags[orgName][compName][siteName] = Array.from(new Set([...existingTags, ...newTags]));

      return updatedTags;
    });

    setSelectedTags([]); // Reset selected tags after adding
  };

  const removeTag = (orgName: string, compName: string, siteName: string, tag: string) => {
    setTags((prevTags) => {
      const updatedTags = { ...prevTags };

      // Remove the tag from the specific website
      const existingTags = updatedTags[orgName]?.[compName]?.[siteName] || [];
      updatedTags[orgName][compName][siteName] = existingTags.filter(t => t !== tag);

      return updatedTags;
    });
  };

  const styles = {
    container: {
      fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      margin: '20px',
      padding: '20px',
      borderRadius: '8px',
      backgroundColor: '#FFFFFF',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      maxWidth: '900px',
    },
    organization: {
      marginBottom: '30px',
      padding: '15px',
      borderRadius: '8px',
      backgroundColor: '#F4F4F4',
    },
    company: {
      marginLeft: '20px',
      marginBottom: '20px',
      padding: '15px',
      borderRadius: '8px',
      backgroundColor: '#FFFFFF',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
    },
    website: {
      marginLeft: '20px',
      marginBottom: '10px',
      padding: '10px',
      borderRadius: '6px',
      backgroundColor: '#F9FAFB',
      border: '1px solid #E5E7EB',
    },
    button: {
      marginTop: '10px',
    },
    buttonRemove: {
      marginLeft: '10px',
      padding: '4px 8px',
      borderRadius: '50%',
      border: 'none',
      backgroundColor: '#EF4444',
      color: 'white',
      cursor: 'pointer',
      fontWeight: 'bold',
    },
    tag: {
      display: 'inline-block',
      padding: '6px 12px',
      margin: '5px 5px 0 0',
      borderRadius: '50px',
      backgroundColor: '#D1FAE5',
      color: '#065F46',
      fontWeight: 'bold',
      fontSize: '14px',
    },
    tagContainer: {
      marginTop: '10px',
      display: 'flex',
      flexWrap: 'wrap' as 'wrap',
    },
    heading: {
      color: '#111827',
      marginBottom: '20px',
    },
    subHeading: {
      color: '#1F2937',
      marginBottom: '15px',
    },
  };

  return (
    <div style={{ width: '100%', flex: '1', padding:' 3rem'    }} >
      <h1 style={styles.heading}>Tag Manager</h1>
      {data.map((org) => (
        <div key={org.organization} style={styles.organization}>
          <h2 style={styles.subHeading}>{org.organization}</h2>
          {org.companies.map((comp) => (
            <div key={comp.companyName} style={styles.company}>
              <h3>{comp.companyName}</h3>
              {comp.websites.map((site) => (
                <div key={site.siteName} style={styles.website}>
                  <h4>{site.siteName}</h4>
                  <div style={styles.tagContainer}>
                    {tags[org.organization]?.[comp.companyName]?.[site.siteName]?.map((tag) => (
                      <span key={tag} style={styles.tag}>
                        {tag}
                        <button
                          style={styles.buttonRemove}
                          onClick={() => removeTag(org.organization, comp.companyName, site.siteName, tag)}
                        >
                          ×
                        </button>
                      </span>
                    )) || <span>No Tags</span>}
                  </div>
                  <MultiSelect
                    data={tags[org.organization]?.tags || []} // Show all tags at the organization level
                    placeholder="Select or add tags"
                    value={selectedTags}
                    onChange={setSelectedTags}
                    creatable
                    searchable
                    getCreateLabel={(query) => `+ Create "${query}"`}
                    onCreate={(query) => {
                      const newTag = query.trim();
                      setSelectedTags((current) => [...current, newTag]);
                      return newTag; // Return the new tag string
                    }}
                    style={{ marginTop: '10px', marginBottom: '10px' }}
                  />
                  <Button
                    style={styles.button}
                    onClick={() => handleAddTag(org.organization, comp.companyName, site.siteName)}
                  >
                    Add Tag
                  </Button>
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default TagManager;
